import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, MessageSquare, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    requirement: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/inquiries`, {
        ...formData,
        inquiry_type: 'contact',
      });
      toast.success('Thank you! Your inquiry has been submitted successfully.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        requirement: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const whatsappNumber = '918169230489';
  const whatsappMessage = 'Hi SwiftHampers, I would like to inquire about your corporate gifting solutions.';
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

  return (
    <div>
      <section className="py-24 md:py-32 bg-gradient-to-br from-surface via-background to-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="text-xs font-medium tracking-widest uppercase text-muted mb-4" data-testid="contact-label">
              Get in Touch
            </div>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight leading-tight mb-6" data-testid="contact-title">
              Let's <span className="text-accent">Connect</span>
            </h1>
            <p className="text-lg md:text-xl text-muted max-w-3xl mx-auto" data-testid="contact-description">
              Have a project in mind? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-3xl font-semibold mb-6" data-testid="contact-info-title">Contact Information</h2>
                <div className="space-y-4">
                  <div className="flex items-start space-x-4" data-testid="contact-email-info">
                    <div className="w-12 h-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center flex-shrink-0">
                      <Mail size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Email</h3>
                      <a href="mailto:sanket@swifthampers.com" className="text-muted hover:text-accent transition-colors">
                        sanket@swifthampers.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4" data-testid="contact-phone-info">
                    <div className="w-12 h-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center flex-shrink-0">
                      <Phone size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Phone</h3>
                      <a href="tel:+918169230489" className="text-muted hover:text-accent transition-colors">+91 81692 30489</a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4" data-testid="contact-location-info">
                    <div className="w-12 h-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center flex-shrink-0">
                      <MapPin size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Service Area</h3>
                      <p className="text-muted">Pan-India Delivery Available</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-accent/5 border border-accent/20 rounded-xl p-6">
                <h3 className="font-semibold mb-3 flex items-center" data-testid="whatsapp-section-title">
                  <MessageSquare className="mr-2 text-accent" size={24} />
                  Quick Inquiry via WhatsApp
                </h3>
                <p className="text-sm text-muted mb-4" data-testid="whatsapp-description">
                  Need immediate assistance? Connect with us on WhatsApp for quick responses.
                </p>
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-button"
                  className="inline-flex items-center h-12 px-6 rounded-full bg-accent text-white font-medium hover:scale-105 transition-transform duration-200 shadow-lg shadow-accent/20"
                >
                  <MessageSquare className="mr-2" size={20} />
                  Chat on WhatsApp
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <form onSubmit={handleSubmit} className="bg-white border border-slate-100 rounded-2xl p-8 shadow-xl" data-testid="contact-form">
                <h2 className="text-2xl font-semibold mb-6" data-testid="contact-form-title">Send us a Message</h2>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      data-testid="contact-form-name"
                      className="w-full h-12 bg-slate-50 border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 rounded-lg px-4 outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="contact-form-email"
                      className="w-full h-12 bg-slate-50 border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 rounded-lg px-4 outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium mb-2">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      data-testid="contact-form-phone"
                      className="w-full h-12 bg-slate-50 border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 rounded-lg px-4 outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor="company" className="block text-sm font-medium mb-2">
                      Company *
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                      data-testid="contact-form-company"
                      className="w-full h-12 bg-slate-50 border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 rounded-lg px-4 outline-none transition-all"
                    />
                  </div>

                  <div>
                    <label htmlFor="requirement" className="block text-sm font-medium mb-2">
                      Requirement *
                    </label>
                    <textarea
                      id="requirement"
                      name="requirement"
                      value={formData.requirement}
                      onChange={handleChange}
                      required
                      rows={5}
                      data-testid="contact-form-requirement"
                      className="w-full bg-slate-50 border border-slate-200 focus:border-accent focus:ring-2 focus:ring-accent/20 rounded-lg px-4 py-3 outline-none transition-all resize-none"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-form-submit"
                    className="w-full h-12 rounded-full bg-accent text-white font-medium hover:scale-105 transition-transform duration-200 shadow-xl shadow-accent/20 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      'Sending...'
                    ) : (
                      <>
                        <Send className="mr-2" size={20} />
                        Send Message
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
